/**
 * 精算タブ。
 *
 * スプリの中心となる画面。送金プランの中で「制約を無効化」を切り替えると、
 * 制約を効かせた場合と、制約が無かった場合を見比べられる。
 */
import {
  buildShareText,
  findMyMember,
  toBalanceRows,
  toTransferRows,
  useConfirmSettlement,
  useDeleteSettlement,
  useFeatures,
  useRecordSettlement,
  useSettlement,
  useSpli,
} from "@spli/app-logic";
import { formatMoney, type GroupDto } from "@spli/core";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AdSlot } from "../../components/AdSlot.js";
import { LoginUpsell } from "../../components/LoginUpsell.js";
import { PayoutAccountView } from "../../components/PayoutAccountView.js";
import { EmptyState, ErrorView, LoadingView } from "../../components/StatusViews.js";
import { Icon } from "../../components/Icon.js";

export function SettlementView({ group }: { group: GroupDto }) {
  /** 制約を一切効かせずに計算するか (「制約を無効化」のチェック) */
  const [ignoreConstraints, setIgnoreConstraints] = useState(false);
  const settlementQuery = useSettlement(group.shareToken, ignoreConstraints);
  const recordSettlement = useRecordSettlement(group.shareToken);
  const deleteSettlement = useDeleteSettlement(group.shareToken);
  const confirmSettlement = useConfirmSettlement(group.shareToken);
  const { user } = useSpli();
  const features = useFeatures();
  const myMember = findMyMember(group.members, user?.id ?? null);
  const [copied, setCopied] = useState(false);
  /** 直前に記録した送金 (取り消し導線を出すため) */
  const [justRecorded, setJustRecorded] = useState<{ id: string; label: string } | null>(null);
  /** 取消の確認を出している精算記録 */
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);

  /**
   * 「受け取り済みにする / 取り消す」を操作できるか。
   *
   * 受取人がアカウントを連携している場合、お金が届いたかを知っているのは
   * 本人だけなので、記録も本人に限定する (サーバー側でも同じ判定をしている)。
   * 連携していない場合は、共有 URL を知っている人が記録できる。
   */
  const canSettle = (recipient: { userId: string | null } | undefined): boolean => {
    if (!recipient?.userId) return true;
    return recipient.userId === user?.id;
  };

  if (settlementQuery.isPending) return <LoadingView label="精算表を計算しています…" />;
  if (settlementQuery.isError) return <ErrorView error={settlementQuery.error} />;

  const settlement = settlementQuery.data.settlement;
  const transfers = toTransferRows(settlement.transfers, group.members, group.currency);
  const balances = toBalanceRows(settlement.balances, group.members, group.currency);
  const maxAbsNet = Math.max(1, ...balances.map((b) => Math.abs(b.net)));

  const share = async (): Promise<void> => {
    const text = buildShareText(group, settlement);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // クリップボードが使えない場合は何もしない
    }
  };

  return (
    <>
      {/*
        ログインで何ができるようになるかを伝える (受取先が無効なら受取記録のみ)。
        送金プランを見て「受け取りを記録したい」と思う前に目に入るよう、上に置く。
      */}
      {transfers.length > 0 && !user && (
        <>
          {features.payoutAccounts ? (
            <LoginUpsell
              icon="bank"
              title="ログインすると送金先を表示できます"
              description="「メンバー」タブで自分を紐づけると、自分への送金に受取口座が表示され、受け取り完了も記録できます。"
            />
          ) : (
            <LoginUpsell
              icon="check-circle"
              title="ログインすると受け取りを記録できます"
              description="「メンバー」タブで自分を紐づけると、自分宛の送金に「受け取りました」の記録をつけられます。"
            />
          )}
        </>
      )}

      {settlement.warnings.map((warning) => (
        <p key={warning} className="notice" style={{ marginBottom: 12 }}>
          ℹ️ {warning}
        </p>
      ))}

      <section className="card">
        <div className="card__title">
          <span>送金プラン</span>
          <span className="badge">{transfers.length} 回</span>
        </div>

        {justRecorded && (
          <p className="notice" style={{ marginBottom: 12 }} role="status">
            <Icon name="check-circle" size={18} /> 「{justRecorded.label}」を記録しました。{" "}
            <button
              type="button"
              className="button button--ghost button--small"
              disabled={deleteSettlement.isPending}
              onClick={() =>
                deleteSettlement.mutate(justRecorded.id, {
                  // 消えてから通知を閉じる。失敗したらエラーを出したまま残す
                  onSuccess: () => setJustRecorded(null),
                })
              }
            >
              取り消す
            </button>
          </p>
        )}

        {recordSettlement.isError && <ErrorView error={recordSettlement.error} />}
        {deleteSettlement.isError && <ErrorView error={deleteSettlement.error} />}

        {transfers.length === 0 ? (
          <EmptyState icon="celebrate">精算は完了しています。</EmptyState>
        ) : (
          <div className="list">
            {transfers.map((transfer) => {
              const recipient = group.members.find((m) => m.id === transfer.toMemberId);
              const isToMe = Boolean(myMember && transfer.toMemberId === myMember.id);

              return (
                <div key={transfer.key}>
                  <article className={`transfer${transfer.isRelay ? " transfer--relay" : ""}`}>
                    <div className="transfer__flow">
                      <div className="transfer__names">
                        {transfer.fromName} <span aria-label="から">→</span> {transfer.toName}
                      </div>
                      {transfer.note && <div className="transfer__note">{transfer.note}</div>}
                    </div>
                    <span className="list-item__amount">{transfer.amountLabel}</span>
                    {canSettle(recipient) ? (
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        disabled={recordSettlement.isPending}
                        onClick={() =>
                          recordSettlement.mutate(
                            {
                              fromMemberId: transfer.fromMemberId,
                              toMemberId: transfer.toMemberId,
                              amount: transfer.amount,
                            },
                            {
                              // 押した直後に一覧から消えるため、取り消せることを伝える
                              onSuccess: (updated) =>
                                setJustRecorded({
                                  id: updated.settlements[0]?.id ?? "",
                                  label: `${transfer.fromName} → ${transfer.toName} ${transfer.amountLabel}`,
                                }),
                            },
                          )
                        }
                      >
                        {isToMe ? "受け取り済み" : "送金済み"}
                      </button>
                    ) : (
                      /* 受取人が連携済みなら、届いたかを知っているのは本人だけ */
                      <span className="muted" style={{ maxWidth: "7em", textAlign: "right" }}>
                        {transfer.toName}さん本人が記録します
                      </span>
                    )}
                  </article>

                  {/* 受取人がアカウントを紐づけていれば、送金先をその場で表示する */}
                  {recipient?.payoutAccount && (
                    <PayoutAccountView account={recipient.payoutAccount} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {settlement.covers.length > 0 && (
          <p className="muted" style={{ marginTop: 12 }}>
            {settlement.covers.map((cover) => {
              const nameOf = (id: string) =>
                group.members.find((m) => m.id === id)?.name ?? "?";
              return (
                <span key={`${cover.covererId}-${cover.coveredId}`} style={{ display: "block" }}>
                  <Icon name="handshake" size={16} /> {nameOf(cover.coveredId)} の分{" "}
                  {formatMoney(cover.amount, group.currency)} は {nameOf(cover.covererId)}{" "}
                  が負担しています。
                </span>
              );
            })}
          </p>
        )}

        {/*
          プランの操作を 1 行にまとめる。左が「計算のしかたを変える」、
          右が「結果を持ち出す」。

          制約の無効化は「制約を付けなかったらどうなるか」を見るためのもので、
          優先・回避・肩代わりの 3 種類すべてを外す (設定は消さない)。
          制約が 1 つも無いグループでは意味が無いので出さない。
        */}
        <div className="settlement-actions">
          {group.constraints.length > 0 ? (
            <label className="checkbox settlement-actions__toggle">
              <input
                className="checkbox__input"
                type="checkbox"
                checked={ignoreConstraints}
                onChange={(event) => setIgnoreConstraints(event.target.checked)}
              />
              <span className="checkbox__label">制約を無効化</span>
            </label>
          ) : (
            /*
              制約が 1 件も無いと、切り替えても結果は変わらない。
              変わらない操作の代わりに、どこで設定できるかだけを置く。
            */
            <p className="muted settlement-actions__toggle">
              <Link to={`/g/${group.shareToken}/members`}>メンバータブ</Link>
              から制約の追加が可能です
            </p>
          )}
          <button type="button" className="button button--secondary button--small" onClick={share}>
            {copied ? "コピーしました" : "精算結果をコピー"}
          </button>
        </div>
      </section>

      <AdSlot placement="settlement" expenseCount={group.expenses.length} />

      <section className="card">
        <h2 className="card__title">立替と負担</h2>
        <div className="stack">
          {balances.map((balance) => (
            <div key={balance.memberId} className="balance-bar">
              <span className="balance-bar__name">{balance.name}</span>
              <span
                aria-hidden="true"
                style={{
                  height: 8,
                  borderRadius: 4,
                  flex: `0 0 ${Math.round((Math.abs(balance.net) / maxAbsNet) * 40)}%`,
                  background:
                    balance.status === "receive"
                      ? "var(--color-primary)"
                      : balance.status === "pay"
                        ? "var(--color-danger)"
                        : "var(--color-border)",
                }}
              />
              <span className={`balance-bar__value balance-bar__value--${balance.status}`}>
                {balance.status === "even"
                  ? "±0"
                  : `${balance.status === "receive" ? "受取" : "支払"} ${balance.netLabel}`}
              </span>
            </div>
          ))}
        </div>
        <p className="muted" style={{ marginTop: 12 }}>
          計算方法: {settlement.stats.method === "exact" ? "厳密解" : "近似解"} ・ 送金
          {settlement.stats.transferCount} 回
        </p>
      </section>

      {group.settlements.length > 0 && (
        <section className="card">
          <h2 className="card__title">精算済みの記録</h2>
          <div className="list">
            {group.settlements.map((payment) => {
              const nameOf = (id: string) =>
                group.members.find((m) => m.id === id)?.name ?? "(削除済み)";
              const isToMe = Boolean(myMember && payment.toMemberId === myMember.id);
              const label = `${nameOf(payment.fromMemberId)} → ${nameOf(payment.toMemberId)} ${formatMoney(payment.amount, group.currency)}`;

              return (
                <div key={payment.id}>
                  <div className="list-item">
                    <div className="list-item__body">
                      <div className="list-item__title">
                        {nameOf(payment.fromMemberId)} → {nameOf(payment.toMemberId)}
                      </div>
                      <div className="list-item__meta">
                        {payment.settledOn}
                        {payment.confirmedAt ? " ・ 受け取り確認済み" : " ・ 受け取り未確認"}
                      </div>
                    </div>
                    <span className="list-item__amount">
                      {formatMoney(payment.amount, group.currency)}
                    </span>
                    {/* 受け取り確認は受取人本人 (アカウント紐づけ済み) のみ */}
                    {isToMe && !payment.confirmedAt && (
                      <button
                        type="button"
                        className="button button--small"
                        disabled={confirmSettlement.isPending}
                        onClick={() => confirmSettlement.mutate(payment.id)}
                      >
                        受け取りました
                      </button>
                    )}
                    {canSettle(group.members.find((m) => m.id === payment.toMemberId)) && (
                      <button
                        type="button"
                        className="button button--ghost button--small"
                        aria-label={`${label} の記録を取り消す`}
                        aria-expanded={cancelTarget === payment.id}
                        disabled={deleteSettlement.isPending}
                        onClick={() =>
                          setCancelTarget(cancelTarget === payment.id ? null : payment.id)
                        }
                      >
                        取消
                      </button>
                    )}
                  </div>

                  {/*
                    記録を消すと収支が変わり、送金プランが計算し直される。
                    金額や送金先が変わることがあるので、押す前に伝えてから確認を取る。
                  */}
                  {cancelTarget === payment.id && (
                    <div className="notice notice--warning confirm" role="alert">
                      <strong>この記録を取り消すと、送金プランが計算し直されます。</strong>
                      <p style={{ marginTop: 4 }}>
                        取り消したあとに追加された支出も含めて再計算するため、
                        <strong>送金の金額や相手が変わることがあります</strong>
                        (「{label}」がそのまま元に戻るとは限りません)。
                      </p>
                      <div className="row row--end" style={{ marginTop: 12 }}>
                        <button
                          type="button"
                          className="button button--secondary button--small"
                          onClick={() => setCancelTarget(null)}
                        >
                          やめる
                        </button>
                        <button
                          type="button"
                          className="button button--small"
                          disabled={deleteSettlement.isPending}
                          onClick={() =>
                            deleteSettlement.mutate(payment.id, {
                              onSuccess: () => setCancelTarget(null),
                            })
                          }
                        >
                          {deleteSettlement.isPending ? "取り消し中…" : "取り消す"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </>
  );
}
